import { useEffect, useState } from 'react';
import type { IndexPrerequisite } from '../../../components/index-page/types';

/**
 * The case registry's EMPLOYEE rule, stated before the reader meets the refusal.
 *
 * <b>A fact about the ACCOUNT, not a key.</b> Holding every case and evidence permission opens
 * nothing for an account not linked to an employee's personnel record, and the server says so only
 * when a write is attempted. So every index page under a case declares it as a prerequisite: the
 * shell draws the banner and disables Create with the same sentence (CLAUDE.md, "A refusal on the
 * ACCOUNT"). Each page supplies the sentence in its own terms — what THIS page cannot be used for.
 *
 * Seeded MET, and a failed check leaves it met — the prerequisites contract: a banner that
 * appears because a feed failed would state something nobody checked.
 */
export function useEmployeePrerequisite(
  fetch: (signal: AbortSignal) => Promise<{ isEmployee: boolean }>,
  detail: string,
): IndexPrerequisite {
  const [isEmployee, setIsEmployee] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    fetch(controller.signal)
      .then((result) => {
        if (!controller.signal.aborted) setIsEmployee(result.isEmployee);
      })
      .catch(() => undefined);

    return () => controller.abort();
  }, [fetch]);

  return {
    label: 'your employee record',
    met: isEmployee,
    detail,
    to: '/personnel',
    permission: 'personnel.update',
  };
}
